import React, { useState } from 'react';
import styled from 'styled-components';
import { strings } from '/imports/strings';
import { Button } from '/imports/ui/Button';
import { Input } from '/imports/ui/Input';
import { Modal } from '/imports/ui/Modal';
import { css } from '/imports/ui/css';

const ConfirmModalStyled = styled(Modal)`
  .message {
    padding: 1rem 0;
  }
  .modal-title {
    font-size: 1.5rem;
  }
  .option-buttons {
    display: flex;
    justify-content: center;
    gap: 1rem;
  }
  .password {
    margin: 0 0 1rem;
    label { display: block; margin-bottom: 0.25rem; font-weight: 500; }
    .hint { color: ${css.grey3}; font-size: 0.82rem; margin-top: 0.35rem; }
  }
  input {
    display: inline-block;
    width: auto;
    margin-right: 1rem;
  }
`;

interface ConfirmModalProps {
  title?: string;
  body?: string | React.ReactNode;
  onClose(): void;
  // The typed password rides along when `requirePassword` is set; callers that do not ask for one take none.
  onConfirm(password?: string): void;
  confirmText?: string;
  rejectText?: string;
  // For something irreversible: the caller's own password, which the METHOD re-checks. Confirming stays
  // disabled until something is typed, and the modal is left open on a wrong one so it can be retyped.
  requirePassword?: boolean;
}

/**
 * Modal presenting confirm/reject buttons over an optional title and body.
 */
export const ConfirmModal = (props: ConfirmModalProps) => {
  const [password, setPassword] = useState('');
  const ready = !props.requirePassword || !!password;
  const confirm = () => ready && props.onConfirm(props.requirePassword ? password : undefined);
  return (
    <ConfirmModalStyled
      title={props.title || ''}
      onClose={props.onClose}
    >
      <div className="message">{props.body || ''}</div>
      {
        props.requirePassword &&
        <div className="password">
          <label>{strings.ConfirmWithPassword()}</label>
          <Input type="password" value={password} autoFocus autoComplete="off" onChange={(v) => setPassword(v || '')} onEnter={() => confirm()} />
          <div className="hint">{strings.ConfirmWithPasswordHint()}</div>
        </div>
      }
      <div className="option-buttons">
        <Button id="confirm-yes" className="primary" disabled={!ready} onClick={() => confirm()}>{props.confirmText || strings.Yes()}</Button>
        <Button id="confirm-no" className="secondary" onClick={() => props.onClose()}>{props.rejectText || strings.No()}</Button>
      </div>
    </ConfirmModalStyled>
  );
};